(function () {
  /**
   * @typedef {Object} QuoteCustomerData
   * @property {string} [email]
   * @property {string} [firstName]
   * @property {string} [lastName]
   */

  /**
   * @typedef {Object} QuoteActiveAddress
   * @property {string} [address1]
   * @property {string} [city]
   * @property {string} [province]
   * @property {string} [zip]
   * @property {string} [country]
   * @property {string} [firstName]
   * @property {string} [lastName]
   * @property {string} [phone]
   * @property {string} [company]
   */

  /**
   * @typedef {Object} QuoteLineItem
   * @property {string} [variantId]
   * @property {string} [productTitle]
   * @property {string} [sku]
   * @property {number} [quantity]
   * @property {number} [unitPrice]
   * @property {number} [lineTotal]
   */

  /**
   * @typedef {Object} QuoteWebhookData
   * @property {string} title
   * @property {QuoteCustomerData} [customerData]
   * @property {QuoteActiveAddress|null} [activeAddress]
   * @property {string} [notes]
   * @property {QuoteLineItem[]} [items]
   */

  class WebhookLogger {
    /**
     * @param {string} text - Plain text message body
     */
    send(title, text) {
      if (typeof text !== 'string' || text === '') {
        return;
      }

      try {
        const body = JSON.stringify({ title, message: text });

        fetch(this._webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(function (error) {
          console.error('WebhookLogger fetch error', error);
        });
      } catch (error) {
        console.error('WebhookLogger error', error);
      }
    }
  }

  class QuoteWebhookLogger extends WebhookLogger {
    constructor() {
      super();
      this._webhookUrl = '/apps/customer-request?resource=chat-notification';
    }
    /**
     * @param {QuoteWebhookData} data - Data to send to the webhook
     */
    log(data) {
      if (!data) {
        return;
      }

      const markdownText = this._buildMarkdown(data);

      if (!markdownText) {
        return;
      }

      const title =
        data.title == null || data.title === ''
          ? 'Quote event'
          : String(data.title);

      this.send(title, markdownText);
    }

    /**
     * @param {QuoteWebhookData} data
     * @returns {string}
     */
    _buildMarkdown(data) {
      const lines = [];
      const customer = data.customerData || {};
      const address = data.activeAddress || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const notes = data.notes;

      lines.push('### Customer');
      lines.push(
        '- Name: ' + this._fullName(customer.firstName, customer.lastName),
      );
      lines.push('- Email: ' + this._emptyLabel(customer.email));
      lines.push('');
      lines.push('### Shipping address');
      lines.push('- Company: ' + this._emptyLabel(address.company));
      lines.push(
        '- Name: ' + this._fullName(address.firstName, address.lastName),
      );
      lines.push('- Address: ' + this._emptyLabel(address.address1));
      lines.push('- City: ' + this._emptyLabel(address.city));
      lines.push('- Province/State: ' + this._emptyLabel(address.province));
      lines.push('- ZIP: ' + this._emptyLabel(address.zip));
      lines.push('- Country: ' + this._emptyLabel(address.country));
      lines.push('- Phone: ' + this._emptyLabel(address.phone));
      lines.push('');
      lines.push('### Items (' + items.length + ')');

      if (items.length === 0) {
        lines.push('- No items');
      } else {
        for (let i = 0; i < items.length; i++) {
          const item = items[i] || {};
          lines.push(
            '- ' +
              this._emptyLabel(item.productTitle) +
              ' | SKU: ' +
              this._emptyLabel(item.sku) +
              ' | Qty: ' +
              this._emptyLabel(item.quantity) +
              ' | Unit: ' +
              this._emptyLabel(item.unitPrice) +
              ' | Total: ' +
              this._emptyLabel(item.lineTotal),
          );
        }
      }

      if (notes != null && String(notes).trim() !== '') {
        lines.push('');
        lines.push('### Notes');
        lines.push(String(notes));
      }

      return lines.join('\n');
    }

    /**
     * @param {string} firstName
     * @param {string} lastName
     * @returns {string}
     */
    _fullName(firstName, lastName) {
      const parts = [
        this._emptyLabel(firstName),
        this._emptyLabel(lastName),
      ].filter(function (part) {
        return part !== '-';
      });

      return parts.length ? parts.join(' ') : '-';
    }

    /**
     * Placeholder for missing scalar fields; does not alter Markdown inside values.
     * @param {unknown} value
     * @returns {string}
     */
    _emptyLabel(value) {
      if (value == null || value === '') {
        return '-';
      }
      return String(value);
    }
  }

  if (typeof window !== 'undefined') {
    window.QuoteWebhookLogger = QuoteWebhookLogger;
    window.WebhookLogger = WebhookLogger;
  }
})();
